import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError, fileUrl } from "@/api";
import { Btn, Card, Field, TextArea } from "@/components/ui-kit";
import { useAuth } from "@/auth";
import { isProfileComplete } from "@/lib/profile";
import { CreatorProfileView } from "@/components/CreatorProfileView";
import { toast } from "sonner";
import { Instagram, Check, Upload, X, Eye } from "lucide-react";
import { CATEGORY_OPTIONS, SERVICE_OPTIONS } from "@/constants/taxonomy";

const PRICES = ["", "€ (alle 300 €)", "€€ (300–1000 €)", "€€€ (1000 €+)", "Neuvoteltavissa"];
const AVAIL = ["", "Saatavilla uusiin yhteistyöihin", "Rajoitetusti saatavilla", "Ei tällä hetkellä saatavilla"];
const COLLAB_OPTIONS = ["", "Rahallinen yhteistyö", "Tuotepalkkio", "Molemmat"];
const LANGUAGE_OPTIONS = ["Suomi", "Englanti", "Ruotsi", "Muu"];
const MAX_CATEGORIES = 6;
const MAX_PORTFOLIO_IMAGES = 3;
const ACCEPTED_IMG = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const isValidUrl = (v) => {
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};
const selectCls = "w-full rounded-xl bg-white border border-[#E6E1D9] px-4 py-3.5 text-[15px] outline-none focus:border-[#292827] transition-colors appearance-none";
const Label = ({ children }) => <span className="block text-sm font-medium text-[#292827] mb-2">{children}</span>;

function Section({ title, desc, children, first }) {
  return (
    <section className={first ? "" : "pt-8 mt-8 border-t border-[#E6E1D9]"}>
      <h4 className="text-lg md:text-xl font-semibold tracking-tightish text-[#292827]">{title}</h4>
      {desc && <p className="mt-1.5 text-sm text-[#6E675C] leading-relaxed">{desc}</p>}
      <div className="mt-6 space-y-5">{children}</div>
    </section>
  );
}

const FieldGroup = ({ label, children }) => (
  <div>{label && <Label>{label}</Label>}{children}</div>
);

function CheckPill({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${active ? "bg-[#292827] text-white border-[#292827]" : "bg-white text-[#6E675C] border-[#E6E1D9] hover:border-[#292827]"}`}>
      {children}
    </button>
  );
}

function CompletionBar({ percent }) {
  return (
    <Card className="p-6" data-testid="profile-completion">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-medium text-[#6E675C]">Profiilin täydentyminen</span>
        <span className="text-[13px] font-semibold text-[#292827]">{percent}%</span>
      </div>
      <div className="mt-3 h-2 w-full rounded-full bg-[#E9EAEC] overflow-hidden">
        <div className="h-full bg-[#292827] rounded-full transition-all duration-500" style={{ width: `${percent}%` }} />
      </div>
      {percent < 100 && <p className="mt-3 text-xs text-[#968F85]">Täydennä profiilisi erottuaksesi ja saadaksesi enemmän yhteydenottoja.</p>}
      {percent === 100 && <p className="mt-3 text-xs text-emerald-600 flex items-center gap-1"><Check size={13} /> Profiilisi on valmis!</p>}
    </Card>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const isCreator = user.role === "creator";
  const p = user.profile || {};
  const [name, setName] = useState(user.name || "");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);

  const [cf, setCf] = useState({
    profile_photo: p.profile_photo || "", bio: p.bio || "", location: p.location || "",
    content_types: Array.isArray(p.content_types) ? p.content_types : [],
    categories: Array.isArray(p.categories) ? p.categories : [],
    languages: Array.isArray(p.languages) ? p.languages : [],
    price_range: p.price_range || "", availability: p.availability || "", collaboration: p.collaboration || "",
    instagram: p.instagram || "", tiktok: p.tiktok || "", youtube: p.youtube || "",
    remote: p.remote === true,
    show_on_landing: p.show_on_landing === true,
  });
  const [portfolioUrl, setPortfolioUrl] = useState(p.portfolio_url || (Array.isArray(p.portfolio) && p.portfolio[0]?.link) || "");
  const [portfolioImages, setPortfolioImages] = useState(Array.isArray(p.portfolio_images) ? p.portfolio_images : []);
  const [portfolioVideo, setPortfolioVideo] = useState(p.portfolio_video || "");
  const [uploadingPf, setUploadingPf] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [comp, setComp] = useState({
    logo: p.logo || "", company_name: p.company_name || user.name || "", industry: p.industry || "",
    website: p.website || "", description: p.description || "", contact_person: p.contact_person || "",
  });

  const setC = (k, v) => setCf((f) => ({ ...f, [k]: v }));
  const setB = (k, v) => setComp((f) => ({ ...f, [k]: v }));

  const toggleIn = (key, value, max) => setCf((f) => {
    const has = f[key].includes(value);
    if (!has && max && f[key].length >= max) {
      toast.error(`Voit valita enintään ${max} kategoriaa`);
      return f;
    }
    return { ...f, [key]: has ? f[key].filter((x) => x !== value) : [...f[key], value] };
  });

  const onPortfolioFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_PORTFOLIO_IMAGES - portfolioImages.length;
    if (room <= 0) { toast.error(`Enintään ${MAX_PORTFOLIO_IMAGES} kuvaa`); return; }
    const valid = files.filter((f) => ACCEPTED_IMG.includes(f.type)).slice(0, room);
    if (valid.length < files.length) toast.error("Vain JPG, JPEG, PNG ja WEBP -kuvat sallitaan");
    if (!valid.length) return;
    setUploadingPf(true);
    try {
      const uploaded = [];
      for (const f of valid) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/portfolio-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      setPortfolioImages((prev) => [...prev, ...uploaded]);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setUploadingPf(false); }
  };
  const removePortfolioImage = (i) => setPortfolioImages((prev) => prev.filter((_, idx) => idx !== i));

  const onAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED_IMG.includes(file.type)) { toast.error("Vain JPG, JPEG, PNG ja WEBP -kuvat sallitaan"); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (isCreator) setC("profile_photo", data.path); else setB("logo", data.path);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setUploadingAvatar(false); }
  };

  const creatorPercent = () => {
    const checks = [cf.profile_photo, cf.bio, cf.location, cf.content_types.length, cf.categories.length, cf.languages.length, cf.price_range, cf.availability, cf.instagram || cf.tiktok || cf.youtube, portfolioUrl];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  };
  const companyPercent = () => {
    const checks = [comp.company_name, comp.industry, comp.website, comp.description, comp.contact_person, comp.logo];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  };

  const save = async (e) => {
    e.preventDefault();
    if (isCreator && portfolioUrl.trim() && !isValidUrl(portfolioUrl.trim())) {
      toast.error("Anna kelvollinen portfolio-linkki (esim. https://...)");
      return;
    }
    setLoading(true);
    try {
      let payload;
      if (isCreator) {
        payload = {
          ...cf,
          categories: cf.categories.slice(0, MAX_CATEGORIES),
          content_types: cf.content_types,
          languages: cf.languages,
          portfolio_url: portfolioUrl.trim(),
          portfolio_images: portfolioImages,
          portfolio_video: portfolioVideo.trim(),
        };
      } else {
        payload = { ...comp };
      }
      const wasComplete = isProfileComplete(user);
      const nameToSave = isCreator ? name : comp.company_name;
      await api.put("/profile/name", { name: nameToSave });
      const { data } = await api.put("/profile", { profile: payload });
      setUser(data);
      toast.success("Profiili tallennettu!");
      if (!wasComplete && isProfileComplete(data)) {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const avatar = isCreator ? cf.profile_photo : comp.logo;
  const initials = (name || "?").slice(0, 2).toUpperCase();
  const previewCreator = {
    name,
    picture: user.picture,
    profile: {
      ...p, ...cf,
      portfolio_url: portfolioUrl,
      portfolio_images: portfolioImages,
      portfolio_video: portfolioVideo,
    },
  };

  return (
    <div data-testid="profile-page" className="max-w-3xl space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          {isProfileComplete(user) ? (
            <h3 className="text-3xl font-semibold tracking-display text-[#292827]">
              {isCreator ? "Oma profiili" : "Yritysprofiili"}
            </h3>
          ) : (
            <div data-testid="onboarding-heading">
              <h3 className="text-3xl md:text-4xl font-semibold tracking-display text-[#292827]">Viimeistele profiilisi</h3>
              <p className="mt-2 text-base text-[#6E675C] leading-relaxed">Täytä profiilisi tiedot, jotta voit käyttää Creatoria.</p>
            </div>
          )}
        </div>
        {isCreator && (
          <Btn type="button" variant="outline" onClick={() => setPreview(true)} data-testid="preview-profile" className="shrink-0">
            <Eye size={16} /> Näytä profiili
          </Btn>
        )}
      </div>

      <CompletionBar percent={isCreator ? creatorPercent() : companyPercent()} />

      <Card className="p-8 md:p-10">
        <form onSubmit={save}>
          {/* Avatar */}
          <div className="flex items-center gap-5">
            <div className="h-20 w-20 rounded-full bg-[#E9EAEC] border border-[#E6E1D9] flex items-center justify-center overflow-hidden text-lg font-medium text-[#292827] shrink-0">
              {avatar ? <img src={fileUrl(avatar)} alt="" className="h-full w-full object-cover" /> : initials}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <label
                  data-testid="avatar-upload"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-[#292827] border border-[#E6E1D9] hover:border-[#292827] px-6 py-3 text-[15px] font-medium cursor-pointer transition-colors">
                  <Upload size={16} /> {uploadingAvatar ? "Ladataan..." : (isCreator ? "Lataa profiilikuva" : "Lataa logo")}
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" disabled={uploadingAvatar} className="hidden" onChange={onAvatarFile} data-testid="avatar-file-input" />
                </label>
                {avatar && (
                  <button type="button" data-testid="avatar-remove"
                    onClick={() => isCreator ? setC("profile_photo", "") : setB("logo", "")}
                    className="text-sm font-medium text-[#968F85] hover:text-[#292827] transition-colors">
                    Poista kuva
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-[#968F85]">JPG, PNG tai WEBP. Näkyy profiilissasi.</p>
            </div>
          </div>

          {isCreator ? (
            <>
              <Section title="Perustiedot" desc="Kerro lyhyesti kuka olet ja missä toimit." >
                <FieldGroup label="Nimi">
                  <Field value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
                </FieldGroup>
                <FieldGroup label="Lyhyt esittely">
                  <TextArea value={cf.bio} maxLength={500}
                    placeholder="Kerro lyhyesti itsestäsi, millaista sisältöä teet ja millaisten brändien kanssa haluaisit tehdä yhteistyötä"
                    onChange={(e) => setC("bio", e.target.value)} data-testid="profile-bio" />
                  <p className="mt-1.5 text-xs text-[#968F85] text-right">{cf.bio.length}/500</p>
                </FieldGroup>
                <FieldGroup label="Paikkakunta">
                  <Field value={cf.location} onChange={(e) => setC("location", e.target.value)} data-testid="profile-location" />
                </FieldGroup>
                <FieldGroup label="Etäyhteistyöt onnistuvat">
                  <div className="flex gap-2">
                    <CheckPill active={cf.remote === true} onClick={() => setC("remote", true)} testid="remote-yes">Kyllä</CheckPill>
                    <CheckPill active={cf.remote === false} onClick={() => setC("remote", false)} testid="remote-no">Ei</CheckPill>
                  </div>
                </FieldGroup>
              </Section>

              <Section title="Kategoriat & palvelut" desc="Valitse aihealueesi ja palvelut, jotta yritykset löytävät sinut." >
                <FieldGroup label={`Kategoriat (valitse enintään ${MAX_CATEGORIES}) · ${cf.categories.length}/${MAX_CATEGORIES}`}>
                  <div className="flex flex-wrap gap-2" data-testid="categories-group">
                    {CATEGORY_OPTIONS.map((c) => (
                      <CheckPill key={c} active={cf.categories.includes(c)} onClick={() => toggleIn("categories", c, MAX_CATEGORIES)} testid={`category-${c}`}>{c}</CheckPill>
                    ))}
                  </div>
                </FieldGroup>
                <FieldGroup label="Palvelut">
                  <div className="flex flex-wrap gap-2" data-testid="services-group">
                    {SERVICE_OPTIONS.map((s) => (
                      <CheckPill key={s} active={cf.content_types.includes(s)} onClick={() => toggleIn("content_types", s)} testid={`service-${s}`}>{s}</CheckPill>
                    ))}
                  </div>
                </FieldGroup>
                <FieldGroup label="Kielet">
                  <div className="flex flex-wrap gap-2" data-testid="languages-group">
                    {LANGUAGE_OPTIONS.map((l) => (
                      <CheckPill key={l} active={cf.languages.includes(l)} onClick={() => toggleIn("languages", l)} testid={`language-${l}`}>{l}</CheckPill>
                    ))}
                  </div>
                </FieldGroup>
              </Section>

              <Section title="Sosiaalinen media" desc="Linkit näkyvät yrityksille profiilissasi." >
                <div className="space-y-3">
                  <Field placeholder="Instagram – käyttäjänimi tai profiililinkki" value={cf.instagram} onChange={(e) => setC("instagram", e.target.value)} data-testid="profile-instagram" />
                  <Field placeholder="TikTok – käyttäjänimi tai profiililinkki" value={cf.tiktok} onChange={(e) => setC("tiktok", e.target.value)} data-testid="profile-tiktok" />
                  <Field placeholder="YouTube – käyttäjänimi tai kanavalinkki" value={cf.youtube} onChange={(e) => setC("youtube", e.target.value)} data-testid="profile-youtube" />
                </div>
              </Section>

              <Section title="Yhteistyö" desc="Auta yrityksiä ymmärtämään, miten teet yhteistyötä." >
                <div className="grid sm:grid-cols-2 gap-5">
                  <FieldGroup label="Hintahaarukka">
                    <select className={selectCls} value={cf.price_range} onChange={(e) => setC("price_range", e.target.value)} data-testid="profile-price">
                      {PRICES.map((x) => <option key={x} value={x}>{x || "Valitse..."}</option>)}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Saatavuus">
                    <select className={selectCls} value={cf.availability} onChange={(e) => setC("availability", e.target.value)} data-testid="profile-availability">
                      {AVAIL.map((x) => <option key={x} value={x}>{x || "Valitse..."}</option>)}
                    </select>
                  </FieldGroup>
                </div>
                <FieldGroup label="Yhteistyöehdot">
                  <select className={selectCls} value={cf.collaboration} onChange={(e) => setC("collaboration", e.target.value)} data-testid="profile-collaboration">
                    {COLLAB_OPTIONS.map((x) => <option key={x} value={x}>{x || "Valitse..."}</option>)}
                  </select>
                </FieldGroup>
              </Section>

              <Section title="Portfolio" desc="Näytä osaamisesi. Tähän voit lisätä myös still-kuvia videoistasi." >
                <FieldGroup label="Portfolio-linkki (valinnainen)">
                  <Field placeholder="Julkinen portfolio-linkki (Canva, nettisivu, Behance, Linktree, Google Drive…)"
                    value={portfolioUrl} onChange={(e) => setPortfolioUrl(e.target.value)} data-testid="profile-portfolio-url" />
                  <p className="mt-1.5 text-xs text-[#968F85]">Yksi julkinen linkki. Näytetään profiilissasi "Avaa portfolio" -painikkeena.</p>
                </FieldGroup>
                <FieldGroup label={`Portfolio-kuvat (valinnainen, enintään ${MAX_PORTFOLIO_IMAGES}) · ${portfolioImages.length}/${MAX_PORTFOLIO_IMAGES}`}>
                  <div className="flex flex-wrap gap-3" data-testid="portfolio-images">
                    {portfolioImages.map((src, i) => (
                      <div key={i} className="relative h-24 w-24 rounded-xl overflow-hidden border border-[#E6E1D9]" data-testid="portfolio-image">
                        <img src={fileUrl(src)} alt="" className="h-full w-full object-cover" />
                        <button type="button" onClick={() => removePortfolioImage(i)} data-testid="remove-portfolio-image"
                          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 flex items-center justify-center text-[#292827] hover:bg-white"><X size={14} /></button>
                      </div>
                    ))}
                    {portfolioImages.length < MAX_PORTFOLIO_IMAGES && (
                      <label className="h-24 w-24 rounded-xl border border-dashed border-[#E6E1D9] flex flex-col items-center justify-center gap-1 text-[#968F85] cursor-pointer hover:border-[#292827] hover:text-[#292827] transition-colors" data-testid="portfolio-image-upload">
                        <Upload size={18} /><span className="text-xs">{uploadingPf ? "Ladataan..." : "Lisää"}</span>
                        <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple disabled={uploadingPf} className="hidden" onChange={onPortfolioFiles} />
                      </label>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-[#968F85]">Tähän voit lisätä myös still-kuvia videoistasi. JPG, PNG tai WEBP – näytetään profiilissasi kuvagalleriana.</p>
                </FieldGroup>
                <FieldGroup label="Portfolio-video (valinnainen)">
                  <Field placeholder="YouTube- tai Vimeo-linkki (esim. https://youtu.be/…)"
                    value={portfolioVideo} onChange={(e) => setPortfolioVideo(e.target.value)} data-testid="profile-portfolio-video" />
                  <p className="mt-1.5 text-xs text-[#968F85]">Lisää linkki – video upotetaan profiiliisi katseltavaksi.</p>
                </FieldGroup>
              </Section>

              <Section title="Näkyvyys" desc="Valinnainen. Voit muuttaa tätä milloin tahansa.">
                <label className="flex items-start gap-3 text-[15px] text-[#3A3733] cursor-pointer select-none">
                  <input type="checkbox" checked={cf.show_on_landing} onChange={(e) => setC("show_on_landing", e.target.checked)}
                    data-testid="profile-show-on-landing"
                    className="mt-1 h-4 w-4 rounded border-[#C7C0B5] accent-[#292827] shrink-0" />
                  <span>Haluan, että profiilini voidaan näyttää Creatorin etusivulla palvelun esittelytarkoituksessa.</span>
                </label>
              </Section>
            </>
          ) : (
            <Section title="Yrityksen tiedot" desc="Kerro yrityksestäsi, jotta sisällöntuottajat tuntevat brändisi." >
              <FieldGroup label="Yrityksen nimi">
                <Field value={comp.company_name} onChange={(e) => setB("company_name", e.target.value)} data-testid="profile-company" />
              </FieldGroup>
              <FieldGroup label="Yhteyshenkilön nimi">
                <Field value={comp.contact_person} onChange={(e) => setB("contact_person", e.target.value)} data-testid="profile-contact" />
              </FieldGroup>
              <div className="grid sm:grid-cols-2 gap-5">
                <FieldGroup label="Toimiala">
                  <Field value={comp.industry} onChange={(e) => setB("industry", e.target.value)} data-testid="profile-industry" />
                </FieldGroup>
                <FieldGroup label="Verkkosivu">
                  <Field value={comp.website} onChange={(e) => setB("website", e.target.value)} data-testid="profile-website" />
                </FieldGroup>
              </div>
              <FieldGroup label="Yrityksen kuvaus">
                <TextArea value={comp.description} onChange={(e) => setB("description", e.target.value)} data-testid="profile-description" />
              </FieldGroup>
            </Section>
          )}

          <div className="pt-8 mt-8 border-t border-[#E6E1D9]">
            <Btn type="submit" disabled={loading} data-testid="profile-save">
              {loading ? "Tallennetaan..." : "Tallenna profiili"}
            </Btn>
          </div>
        </form>
      </Card>

      {isCreator && (cf.instagram || cf.tiktok || cf.youtube) && (
        <p className="text-xs text-[#968F85] flex items-center gap-1.5 px-1"><Instagram size={13} /> Sosiaaliset linkit näkyvät yrityksille profiilissasi.</p>
      )}

      {preview && isCreator && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
          data-testid="profile-preview-modal" onClick={() => setPreview(false)}>
          <div className="relative w-full max-w-3xl my-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-white/90">Esikatselu – näin yritykset näkevät profiilisi</span>
              <button onClick={() => setPreview(false)} data-testid="close-preview"
                className="h-9 w-9 rounded-full bg-white flex items-center justify-center text-[#292827] hover:bg-white/90 transition-colors"><X size={18} /></button>
            </div>
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl">
              <CreatorProfileView creator={previewCreator} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
